import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LBtn, LHero } from "@components";
import { NavItems } from "@helpers/navigation";
import { useApi } from "@services/useApi";
import { AssessmentLinkDetails } from "@services/apiService";
import { REMOTE_ASSESSMENT_LINK_TOKEN_KEY } from "./assessmentWelcome";
import styles from "./remoteAssessmentLanding.module.css";

const DATE_OF_BIRTH_VALIDATION_MESSAGE = "Please enter a valid date of birth using MM DD YYYY.";

export const RemoteAssessmentLanding = (): JSX.Element => {
    const { token } = useParams();
    const assessmentToken = getAssessmentToken(token);
    const navigate = useNavigate();
    const { getAssessmentLink, confirmAssessmentLinkIdentity, loading } = useApi();
    const [details, setDetails] = useState<AssessmentLinkDetails>();
    const [error, setError] = useState("");
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [birthMonth, setBirthMonth] = useState("");
    const [birthDay, setBirthDay] = useState("");
    const [birthYear, setBirthYear] = useState("");
    const [attemptsLeft, setAttemptsLeft] = useState(3);
    const [confirmationMessage, setConfirmationMessage] = useState("");

    useEffect(() => {
        const loadAssessmentLink = async (): Promise<void> => {
            if (!assessmentToken) {
                setError("This assessment link is invalid.");
                return;
            }

            try {
                setDetails(await getAssessmentLink(assessmentToken));
            } catch {
                setError("This assessment link is invalid or has already been used.");
            }
        };

        void loadAssessmentLink();
    }, [assessmentToken]);

    const handleBeginAssessment = (): void => {
        if (!assessmentToken) {
            setError("This assessment link is invalid.");
            return;
        }

        setIsConfirmationOpen(true);
    };

    const handleCancelConfirmation = (): void => {
        setIsConfirmationOpen(false);
        setConfirmationMessage("");
        setFirstName("");
        setLastName("");
        setBirthMonth("");
        setBirthDay("");
        setBirthYear("");
    };

    const handleConfirmedIdentity = (): void => {
        localStorage.removeItem("assessmentForm");
        sessionStorage.setItem(REMOTE_ASSESSMENT_LINK_TOKEN_KEY, assessmentToken);
        navigate(NavItems.Assessments.Landing.path);
    };

    const handleBirthMonthBlur = (): void => {
        const nextBirthMonth = padDatePart(birthMonth);

        setBirthMonth(nextBirthMonth);
        validateBirthDateFields(nextBirthMonth, birthDay, birthYear);
    };

    const handleBirthDayBlur = (): void => {
        const nextBirthDay = padDatePart(birthDay);

        setBirthDay(nextBirthDay);
        validateBirthDateFields(birthMonth, nextBirthDay, birthYear);
    };

    const handleBirthYearBlur = (): void => {
        validateBirthDateFields(birthMonth, birthDay, birthYear);
    };

    const validateBirthDateFields = (month: string, day: string, year: string, requireComplete = false): boolean => {
        const validationMessage = getBirthDateValidationMessage(month, day, year, requireComplete);

        setConfirmationMessage(validationMessage);

        return !validationMessage;
    };

    const handleSubmitConfirmation = async (event: FormEvent): Promise<void> => {
        event.preventDefault();

        if (!details || !assessmentToken || attemptsLeft === 0) {
            return;
        }

        if (!validateBirthDateFields(birthMonth, birthDay, birthYear, true)) {
            return;
        }

        const birthDate = getBirthDate(birthMonth, birthDay, birthYear);

        setConfirmationMessage("");

        try {
            const response = await confirmAssessmentLinkIdentity(assessmentToken, {
                firstName,
                lastName,
                birthDate
            });

            if (response.isConfirmed) {
                handleConfirmedIdentity();
                return;
            }

            setAttemptsLeft(response.attemptsLeft);

            if (response.isLocked) {
                setConfirmationMessage(
                    "We are unable to confirm your identity, please contact your healthcare provider or study researcher."
                );
                return;
            }

            setConfirmationMessage(
                `That information supplied does not match our records. You have ${response.attemptsLeft} attempts left.`
            );
        } catch {
            setAttemptsLeft(0);
            setConfirmationMessage(
                "We are unable to confirm your identity, please contact your healthcare provider or study researcher."
            );
        }
    };

    if (error) {
        return (
            <LHero centered contentCentered size="fullheight">
                <div className={styles.message}>
                    <p className="is-size-4 has-text-weight-semibold">{error}</p>
                </div>
            </LHero>
        );
    }

    if (!details) {
        return (
            <LHero centered contentCentered size="fullheight">
                <div className={styles.message}>
                    <p className="is-size-4 has-text-weight-semibold">Loading assessment link...</p>
                </div>
            </LHero>
        );
    }

    return (
        <main className={styles.page}>
            <header className={styles.header}>
                <div className={styles.brand}>
                    LASSI-D<sup>&trade;</sup>
                </div>
            </header>
            <div className={styles.main}>
                <section className={styles.content}>
                    <div className={styles.body}>
                        <p className={styles.greeting}>Hi {details.examineeFirstName},</p>

                        <p>
                            Here you will find a link to start the LASSI-D<sup>&trade;</sup> cognitive assessment that
                            your healthcare provider or study researcher has recommended that you complete.
                        </p>

                        <p>
                            After you select the &quot;Begin Assessment&quot; button, but before you begin the
                            Assessment itself, you will be shown a brief video with some background information and
                            instructions explaining what you need to know to complete the Assessment. After you complete
                            the Assessment, which will take approx. 15 minutes, the final screen will provide you with
                            next steps.
                        </p>

                        <p>
                            To complete the LASSI-D<sup>&trade;</sup> cognitive assessment you will need:
                        </p>

                        <ol className={styles.requirements}>
                            <li>
                                A device: A computer, a Mac or an Android tablet with a functioning microphone and a
                                functioning loudspeaker.
                            </li>
                            <li>A Wi-Fi connection</li>
                            <li>A quiet room.</li>
                        </ol>

                        <p>Thank you.</p>
                        <p className={styles.clinicName}>{details.clinicName}</p>
                    </div>

                    <aside className={styles.action}>
                        <LBtn onClick={handleBeginAssessment} loading={loading} isBold>
                            Begin Assessment
                        </LBtn>
                    </aside>
                </section>

                <section className={styles.disclaimerSection}>
                    <hr className={styles.divider} />
                    <p className={styles.disclaimer}>
                        LASSI-D<sup>&trade;</sup> is a supportive assessment tool and does not diagnose Alzheimer&apos;s
                        disease, dementia, or any medical condition. Results must be interpreted by qualified healthcare
                        professionals and integrated into a complete clinical evaluation.
                    </p>
                </section>
            </div>
            <footer className={styles.footer}>
                <img className={styles.footerLogo} src="/i-Function_logo-white3.png" alt="i-Function" />
            </footer>
            {isConfirmationOpen && (
                <div className={styles.modalOverlay}>
                    <div
                        className={styles.modal}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="confirmIdentityTitle"
                    >
                        <div className={styles.modalHeader}>
                            <h1 id="confirmIdentityTitle">Confirm Your Identity.</h1>
                        </div>
                        <form onSubmit={handleSubmitConfirmation}>
                            <div className={styles.modalBody}>
                                <p className={styles.modalInstruction}>
                                    Please input your First and Last name, and your birthday.
                                </p>

                                <label className={styles.modalLabel} htmlFor="identity-first-name">
                                    First Name
                                </label>
                                <input
                                    id="identity-first-name"
                                    className={styles.modalInput}
                                    value={firstName}
                                    onChange={(event) => setFirstName(event.target.value)}
                                    disabled={attemptsLeft === 0}
                                    required
                                />

                                <label className={styles.modalLabel} htmlFor="identity-last-name">
                                    Last Name
                                </label>
                                <input
                                    id="identity-last-name"
                                    className={styles.modalInput}
                                    value={lastName}
                                    onChange={(event) => setLastName(event.target.value)}
                                    disabled={attemptsLeft === 0}
                                    required
                                />

                                <p className={styles.modalLabel} id="identity-birth-date-label">
                                    Date of Birth
                                </p>
                                <div className={styles.dateFields} aria-labelledby="identity-birth-date-label">
                                    <input
                                        className={`${styles.modalInput} ${styles.dateInput}`}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        placeholder="MM"
                                        aria-label="Birth month"
                                        value={birthMonth}
                                        onChange={(event) => setBirthMonth(getDatePart(event.target.value, 2))}
                                        onBlur={handleBirthMonthBlur}
                                        disabled={attemptsLeft === 0}
                                        required
                                    />
                                    <input
                                        className={`${styles.modalInput} ${styles.dateInput}`}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        placeholder="DD"
                                        aria-label="Birth day"
                                        value={birthDay}
                                        onChange={(event) => setBirthDay(getDatePart(event.target.value, 2))}
                                        onBlur={handleBirthDayBlur}
                                        disabled={attemptsLeft === 0}
                                        required
                                    />
                                    <input
                                        className={`${styles.modalInput} ${styles.dateInput}`}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        placeholder="YYYY"
                                        aria-label="Birth year"
                                        value={birthYear}
                                        onChange={(event) => setBirthYear(getDatePart(event.target.value, 4))}
                                        onBlur={handleBirthYearBlur}
                                        disabled={attemptsLeft === 0}
                                        required
                                    />
                                </div>

                                {confirmationMessage && (
                                    <p className={styles.confirmationMessage}>{confirmationMessage}</p>
                                )}
                            </div>
                            <div className={styles.modalFooter}>
                                <LBtn
                                    variation="outline"
                                    fullWidth
                                    noSpacing
                                    onClick={handleCancelConfirmation}
                                    disabled={loading}
                                >
                                    Cancel
                                </LBtn>
                                <LBtn fullWidth noSpacing type="submit" loading={loading} disabled={attemptsLeft === 0}>
                                    Confirm
                                </LBtn>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </main>
    );
};

const getAssessmentToken = (token?: string): string => {
    if (!token) {
        return "";
    }

    return token.match(/^[A-Za-z0-9_-]{43}/)?.[0] ?? token;
};

const getDatePart = (value: string, maxLength: number): string => {
    return value.replace(/\D/g, "").slice(0, maxLength);
};

const padDatePart = (value: string): string => {
    return value.length === 1 ? value.padStart(2, "0") : value;
};

const getBirthDate = (month: string, day: string, year: string): string => {
    if (getBirthDateValidationMessage(month, day, year, true)) {
        return "";
    }

    return `${year}-${month}-${day}`;
};

const getBirthDateValidationMessage = (month: string, day: string, year: string, requireComplete = false): string => {
    if (!month && !day && !year && !requireComplete) {
        return "";
    }

    if ((month && !/^\d{2}$/.test(month)) || (day && !/^\d{2}$/.test(day)) || (year && !/^\d{4}$/.test(year))) {
        return requireComplete || year.length === 4 ? DATE_OF_BIRTH_VALIDATION_MESSAGE : "";
    }

    if (requireComplete && (!/^\d{2}$/.test(month) || !/^\d{2}$/.test(day) || !/^\d{4}$/.test(year))) {
        return DATE_OF_BIRTH_VALIDATION_MESSAGE;
    }

    const monthNumber = Number(month);
    const dayNumber = Number(day);
    const yearNumber = Number(year);

    if (month && (monthNumber < 1 || monthNumber > 12)) {
        return DATE_OF_BIRTH_VALIDATION_MESSAGE;
    }

    if (day && (dayNumber < 1 || dayNumber > 31)) {
        return DATE_OF_BIRTH_VALIDATION_MESSAGE;
    }

    if (year && year.length === 4 && yearNumber < 1900) {
        return DATE_OF_BIRTH_VALIDATION_MESSAGE;
    }

    if (/^\d{2}$/.test(month) && /^\d{2}$/.test(day) && /^\d{4}$/.test(year)) {
        const date = new Date(yearNumber, monthNumber - 1, dayNumber);

        if (
            date.getFullYear() !== yearNumber ||
            date.getMonth() !== monthNumber - 1 ||
            date.getDate() !== dayNumber ||
            date > new Date()
        ) {
            return DATE_OF_BIRTH_VALIDATION_MESSAGE;
        }
    }

    return "";
};
